import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/daf7vf1tto0ceqqnh7fg";

function Subscribe() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot guard: silently ignore bot submissions but show normal feedback
    const hp = String(fd.get("contact_alt") || "").trim();
    if (hp) {
      setStatus("success");
      setOkMsg("已收到你的订阅申请，请留意邮箱确认。");
      form.reset();
      return;
    }
    // drop honeypot field from payload
    fd.delete("contact_alt");

    setStatus("loading");
    setFormError("");
    setOkMsg("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const msg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const okCode = parsed?.code === "OK";
      const serverSpam = /spam|form data is spam/i.test(responseText + " " + msg);
      if (res.ok && okCode && !serverSpam) {
        setStatus("success");
        setOkMsg(parsed?.meta?.message || "订阅成功，换新地址后会第一时间提醒你。");
        form.reset();
        return;
      }
      // failure: keep input, show server message
      setStatus("error");
      setFormError(
        serverSpam || /spam/i.test(msg)
          ? "提交未通过校验，请稍后重试。"
          : msg || "提交失败，请检查信息后重试。"
      );
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  }

  return (
    <section id="subscribe" className="scroll-mt-24 bg-foreground-950 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
        <div className="overflow-hidden rounded-2xl bg-background-100">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* text side */}
            <div className="relative flex flex-col justify-center p-7 md:p-12">
              <div className="dot-grid absolute inset-0 opacity-40" aria-hidden="true"></div>
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700 ring-1 ring-primary-200">
                  <i className="ri-notification-4-line"></i>
                  换新地址订阅
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                  留个联系方式，换新地址不再错过
                </h2>
                <p className="mt-3 text-sm leading-6 text-foreground-600">
                  绑定后，每次吃瓜地址执行域名迁移或换新地址，都会第一时间把最新入口推给你，
                  配合收藏本页双保险。
                </p>
                <div className="mt-6 flex flex-wrap gap-3 text-xs text-foreground-600">
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-200 px-2.5 py-1">
                    <i className="ri-mail-line text-primary-600"></i>
                    新地址秒推
                  </span>
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-200 px-2.5 py-1">
                    <i className="ri-shield-check-line text-accent-600"></i>
                    信息仅用于提醒
                  </span>
                </div>
              </div>
            </div>

            {/* form side */}
            <div className="bg-background-50 p-7 md:p-12">
              <form
                data-readdy-form="换新地址订阅提醒"
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-foreground-800">收件邮箱</span>
                  <input
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    placeholder="name@example.com"
                    className="rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
                  />
                </label>

                {/* honeypot, hidden via css class */}
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-ward"
                />

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-foreground-800">常用入口偏好（可选）</span>
                  <select
                    name="channel"
                    className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                  >
                    <option value="主站即可">主站即可</option>
                    <option value="主站 + 备用">主站 + 备用</option>
                    <option value="全部含归档">全部含归档</option>
                  </select>
                </label>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line mr-1 inline-block animate-spin"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-bell-fill mr-1"></i>
                      开启换新提醒
                    </>
                  )}
                </button>

                {status === "success" && (
                  <p className="rounded-md bg-accent-50 px-3 py-2.5 text-sm font-medium text-accent-800">
                    <i className="ri-checkbox-circle-fill mr-1"></i>
                    {okMsg}
                  </p>
                )}
                {status === "error" && (
                  <p className="rounded-md bg-primary-50 px-3 py-2.5 text-sm font-medium text-primary-800">
                    <i className="ri-error-warning-fill mr-1"></i>
                    {formError}
                  </p>
                )}
                <p className="text-xs leading-5 text-foreground-500">
                  提交即表示同意仅用于换新地址提醒用途，可随时退订，无需担心信息滥用。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Subscribe;